import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, notifyErr, fmtMoney } from "@/lib/api";

const PLANS = ["easy", "full", "upper"];
const gross = (v, vr) => Math.round(Number(v || 0) * (1 + vr / 100) * 100) / 100;

function Summary({ p, f }) {
  const diff = Number(f.setup_diff || 0);
  const when = p.stripe_active ? (p.upgrade ? "subito, con conguaglio del periodo in corso" : "dal prossimo rinnovo") : "dal prossimo rinnovo pagato";
  return (
    <div className="text-sm rounded-md border border-zinc-800 p-3 space-y-1" data-testid="plan-change-summary">
      <div>Da {p.from_plan?.toUpperCase()} a <b>{p.to_plan_name}</b> {p.upgrade ? "(passaggio superiore)" : "(passaggio inferiore, attivazione non rimborsata)"}</div>
      <div>Canone attuale {fmtMoney(p.current_monthly)} + IVA, nuovo canone {fmtMoney(f.monthly_fee)} + IVA al mese, {when}</div>
      {diff > 0 ? <div data-testid="plan-change-diff">La società riceve una email per pagare {fmtMoney(diff)} + IVA = {fmtMoney(gross(diff, p.vat_rate))}. Il piano cambia dopo il pagamento.</div>
        : <div data-testid="plan-change-diff">Nessuna differenza da pagare: il piano cambia subito e parte la email di ringraziamento.</div>}
      {p.custom && <div className="text-amber-300">Prezzo personalizzato: controlla differenza e canone.</div>}
    </div>
  );
}

export function PlanChangeDialog({ company, onClose, onDone }) {
  const [plan, setPlan] = useState("");
  const [p, setP] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPlan(""); setP(null); }, [company]);
  useEffect(() => {
    if (!company || !plan) { return; }
    api.get(`/companies/${company.id}/plan-change/preview`, { params: { plan } }).then((r) => { setP(r.data); setF({ setup_diff: r.data.setup_diff, monthly_fee: r.data.monthly_fee }); }).catch((e) => { notifyErr(e); setP(null); });
  }, [company, plan]);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/companies/${company.id}/plan-change`, { plan, setup_diff: Number(f.setup_diff), monthly_fee: Number(f.monthly_fee), origin_url: window.location.origin });
      toast.success(r.data.message); onDone(); onClose();
    } catch (e) { notifyErr(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="plan-change-dialog">
        <DialogHeader><DialogTitle>Cambia piano · {company?.name}</DialogTitle>
          <DialogDescription>Piano attuale {company?.plan?.toUpperCase()}. Nel passaggio a un piano superiore la società paga la differenza del costo di attivazione.</DialogDescription></DialogHeader>
        <div><Label className="text-xs text-zinc-400">Nuovo piano</Label>
          <Select value={plan} onValueChange={setPlan}><SelectTrigger className="mt-1" data-testid="plan-change-select"><SelectValue placeholder="Scegli il piano" /></SelectTrigger>
            <SelectContent>{PLANS.filter((x) => x !== company?.plan).map((x) => <SelectItem key={x} value={x} data-testid={`plan-change-opt-${x}`}>{x.toUpperCase()}</SelectItem>)}</SelectContent></Select></div>
        {p && <>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-zinc-400">Differenza attivazione (€ + IVA)</Label><Input type="number" min="0" step="0.01" value={f.setup_diff} onChange={(e) => setF({ ...f, setup_diff: e.target.value })} className="mt-1" data-testid="plan-change-setup-diff" /></div>
            <div><Label className="text-xs text-zinc-400">Nuovo canone mensile (€ + IVA)</Label><Input type="number" min="0" step="0.01" value={f.monthly_fee} onChange={(e) => setF({ ...f, monthly_fee: e.target.value })} className="mt-1" data-testid="plan-change-monthly-fee" /></div>
          </div>
          <Summary p={p} f={f} />
        </>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="plan-change-cancel">Annulla</Button>
          <Button className="btn-brand" disabled={!p || busy} onClick={submit} data-testid="plan-change-submit">{busy && <Loader2 className="animate-spin mr-1" size={14} />}{Number(f.setup_diff) > 0 ? "Invia email di pagamento" : "Cambia piano"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
